// Loyalty Feature deep-dive sub-pages (matches Antavo /product/loyalty-engine/features/*)
// All 9 feature pages share one template + data-driven content.

import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, ChevronRight, Zap } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import { Button } from '../../components/ui/button';

const FEATURES = {
  'points-economy': {
    title: 'Points Economy — Earn, Burn, Liability & Breakage',
    subtitle: 'Configure global earn rules, country-specific overrides, segment-specific multipliers and real-time liability tracking — all in one operator-friendly UI.',
    metaDesc: 'Design and run a sustainable loyalty points economy with Fundle.ai. Earn-burn ratios, liability forecasting, breakage modelling and segment multipliers — built for Indian retail.',
    kw: 'loyalty points economy, points earn burn, loyalty liability management, points breakage retail',
    capabilities: ['Global, country and brand-level earn rule configuration', 'Real-time liability + outstanding-points dashboards', 'Burn-rate alerts when redemption falls below 30%', 'Breakage modelling and quarterly expiry automation', 'Segment-specific point multipliers (Champions get 2x, At-Risk get 3x)', 'Currency-aware earning across multi-country operations'],
    bigStat: { v: '30-45%', l: 'healthy earn-burn ratio range' },
  },
  'tier-management': {
    title: 'Tier Management — Silver, Gold, Platinum & Beyond',
    subtitle: 'Multi-tier programmes with rolling qualification windows, downgrade protection and AI-suggested upgrade nudges. Localise per country, brand or segment without rebuilding.',
    metaDesc: 'Design effective loyalty tier structures with Fundle.ai. Qualification windows, downgrade protection, upgrade nudges and AI-suggested tier benefits for Indian retail.',
    kw: 'loyalty tier management, Silver Gold Platinum loyalty, tier qualification rolling window, downgrade protection',
    capabilities: ['Rolling qualification windows (90/180/365 day)', 'Downgrade protection & grace periods', 'AI upgrade nudges (you\'re ₹420 away from Gold)', 'Tier-specific benefit catalogues', 'Multi-tier visualisation in WhatsApp + app', 'A/B testing of tier thresholds'],
    bigStat: { v: '3-7 tiers', l: 'optimal range for India retail' },
  },
  'workflows': {
    title: 'Workflows — Visual Loyalty Logic Builder',
    subtitle: 'A drag-and-drop visual editor for loyalty logic. Build complex earn rules, burn flows, gamification mechanics and lifecycle journeys without writing a line of code.',
    metaDesc: 'Build loyalty workflows visually with Fundle.ai. Drag-and-drop logic editor for earn rules, burn flows, gamification and lifecycle journeys — no code required.',
    kw: 'loyalty workflows, visual loyalty editor, no code loyalty rules, loyalty automation builder',
    capabilities: ['Drag-and-drop visual logic editor', 'Pre-built templates for 50+ common loyalty mechanics', 'Conditional branching (if/then/else)', 'Multi-step journeys with delay nodes', 'Webhook + API node for any external trigger', 'Version control + staging environments'],
    bigStat: { v: '50+', l: 'pre-built workflow templates' },
  },
  'rewards': {
    title: 'Rewards Catalog — Beyond Discounts',
    subtitle: 'Build a rewards catalogue tailored to your customers — experiential, partner, branded and digital rewards. Generate, customise and send out millions of rewards in minutes.',
    metaDesc: 'Build a powerful loyalty rewards catalogue with Fundle.ai. Experiential, partner, branded and digital rewards for Indian retail — millions of rewards generated in minutes.',
    kw: 'loyalty rewards catalog, experiential rewards, branded rewards, digital rewards platform',
    capabilities: ['Catalogue builder with experiential, partner and digital reward types', 'Bulk reward generation (millions in minutes)', 'Reward inventory tracking + auto-refresh', 'Partner reward integration (Amazon, Flipkart, BMS, Uber)', 'AI-suggested rewards per customer', 'Reward redemption analytics + cost-per-redemption'],
    bigStat: { v: '500+', l: 'partner rewards available' },
  },
  'clubs': {
    title: 'Clubs — Group & Family Loyalty',
    subtitle: 'Turn loyalty into a group adventure. Clubs let customers collaborate, pool points and reach milestones as a team — perfect for family memberships and corporate loyalty.',
    metaDesc: 'Group loyalty programmes with Fundle.ai Clubs. Family memberships, corporate loyalty, point pooling and team milestones — drive community-led loyalty engagement.',
    kw: 'group loyalty programme, family loyalty, club loyalty, point pooling, corporate loyalty',
    capabilities: ['Multi-member club creation with admin roles', 'Shared points wallet across club members', 'Club-level tier qualification', 'Group milestone challenges and rewards', 'Family tree visualisation', 'Corporate club programmes for B2B2C'],
    bigStat: { v: '+22%', l: 'lift in engagement vs. solo members' },
  },
  'challenges': {
    title: 'Challenges — Behavioural Loyalty Mechanics',
    subtitle: 'Drive specific customer behaviours with time-bound challenges. Combine transactions, soft actions and social engagement into a single mechanic that drives 3x participation.',
    metaDesc: 'Create behavioural loyalty challenges with Fundle.ai. Combine transactions, soft actions and social engagement into time-bound mechanics — 3x participation uplift proven.',
    kw: 'loyalty challenges, behavioural loyalty, retail challenges, gamified challenges',
    capabilities: ['Time-bound challenge builder (daily/weekly/monthly)', 'Mixed-action challenges (visit + buy + share)', 'Progressive challenges with tiered rewards', 'Leaderboards for competitive cohorts', 'Surprise unlock mechanics', 'Auto-graduation across challenge series'],
    bigStat: { v: '3x', l: 'participation uplift vs static programmes' },
  },
  'mobile-wallet': {
    title: 'Mobile Wallet — Apple & Google Pass Integration',
    subtitle: 'Replace plastic loyalty cards with always-on Apple Wallet and Google Pay Passes. Push location-based notifications without requiring a separate app install.',
    metaDesc: 'Digital loyalty cards via Apple Wallet and Google Pay with Fundle.ai. Always-on, push-enabled, location-aware loyalty without forcing customers to install your app.',
    kw: 'digital loyalty card, Apple Wallet loyalty, Google Pay loyalty, mobile wallet passes',
    capabilities: ['Apple Wallet + Google Pay pass generation', 'Auto-update pass content (tier, balance, expiry)', 'Geofenced push notifications via the pass', 'Branded pass design with brand colours + logo', 'Pass-based offer redemption at POS', 'Analytics on pass installs + active usage'],
    bigStat: { v: 'No app', l: 'required for customers to participate' },
  },
  'gamification-and-referrals': {
    title: 'Gamification & Referrals — Spin, Streak, Refer-a-Friend',
    subtitle: 'No-code gamification toolkit. Spin-to-win, scratch cards, streaks, badges, leaderboards and referral chains — proven to deliver 3x engagement uplift.',
    metaDesc: 'No-code loyalty gamification with Fundle.ai. Spin-to-win, scratch cards, streaks, badges, leaderboards and referral chains — 3x engagement uplift across Indian retail.',
    kw: 'loyalty gamification, spin to win, scratch card loyalty, referral programme retail, loyalty streaks',
    capabilities: ['Spin-to-win, scratch card, dice and prize wheel mechanics', 'Streak rewards (visit 7 days in a row…)', 'Badge system with progressive unlocks', 'Refer-a-friend chains with double-sided incentives', 'Anti-gaming + fraud detection', 'Public + private leaderboards'],
    bigStat: { v: '3x', l: 'engagement uplift typical' },
  },
  'receipt-scanning': {
    title: 'Receipt Scanning — Loyalty Without POS Integration',
    subtitle: 'Reward customers for purchases at indirect channels (third-party retailers, distributor outlets, traditional trade). AI extracts SKU, value and store from any receipt photo.',
    metaDesc: 'AI-powered receipt scanning loyalty with Fundle.ai. Reward customers for purchases at indirect channels — SKU extraction, fraud detection, instant point credit.',
    kw: 'receipt scanning loyalty, OCR receipt loyalty, indirect channel loyalty, AI receipt scanning',
    capabilities: ['AI OCR with 96%+ accuracy on Indian receipts', 'SKU + brand + store + amount extraction', 'Multi-retailer support (D-Mart, More, Reliance Fresh…)', 'Duplicate-receipt fraud detection', 'Manual review queue for low-confidence scans', 'Instant point credit on approval'],
    bigStat: { v: '96%+', l: 'OCR accuracy on Indian retail receipts' },
  },
};

const FeaturePage = () => {
  const { slug } = useParams();
  const f = FEATURES[slug];
  if (!f) return <Navigate to="/products/loyalty" replace />;
  return (
    <div className="min-h-screen bg-white">
      <SEOHead title={`${f.title} | Fundle.ai Loyalty Platform`} description={f.metaDesc} keywords={f.kw} canonical={`https://fundle.ai/products/loyalty/features/${slug}`} />
      <section className="pt-32 pb-12 bg-[#F4F6FB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#0B1437]">Home</Link>
            <ChevronRight size={14} />
            <Link to="/products/loyalty" className="hover:text-[#0B1437]">Loyalty</Link>
            <ChevronRight size={14} />
            <span className="text-[#0B1437]">Features</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B1437] mb-5 leading-tight" data-testid="feature-heading">{f.title}</h1>
          <p className="text-lg text-[#4A5878] max-w-3xl leading-relaxed mb-8">{f.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button size="lg" className="rounded-full bg-[#0B1437] hover:bg-[#050A1F] text-white px-8 py-6">Book a demo <ArrowRight className="ml-2" size={16} /></Button></Link>
            <Link to="/products/loyalty"><Button variant="outline" size="lg" className="rounded-full border-slate-300 text-[#1F2A4A] hover:bg-slate-50 px-8 py-6">Back to Loyalty</Button></Link>
          </div>
        </div>
      </section>

      {/* Stat */}
      <section className="py-14 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-5xl sm:text-6xl font-bold text-white mb-2">{f.bigStat.v}</p>
          <p className="text-slate-400 text-sm">{f.bigStat.l}</p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0B1437] mb-8">Capabilities</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {f.capabilities.map((c, idx) => (
              <div key={idx} className="flex gap-3 p-5 rounded-2xl border border-slate-200" data-testid={`cap-${idx}`}>
                <CheckCircle size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#1F2A4A] leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Other Loyalty Engine features</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(FEATURES).filter(([s]) => s !== slug).map(([s, fx]) => (
              <Link key={s} to={`/products/loyalty/features/${s}`} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-[#D4AF37]/30 text-sm font-semibold text-[#1F2A4A] hover:text-[#0B1437] transition-colors">
                {fx.title.split('—')[0].trim()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <Zap size={28} className="mx-auto text-[#0B1437] mb-3" />
          <h2 className="text-3xl font-bold text-[#0B1437] mb-3">See {f.title.split('—')[0].trim()} in action</h2>
          <p className="text-[#4A5878] mb-6">A 30-minute working session with our solutions team — tailored to your brand or mall.</p>
          <Link to="/book-demo"><Button size="lg" className="rounded-full bg-[#0B1437] hover:bg-[#050A1F] text-white px-8 py-6">Book a demo <ArrowRight className="ml-2" size={16} /></Button></Link>
        </div>
      </section>
    </div>
  );
};

export default FeaturePage;
